import type { ProductDetail } from '../../domain/models/ProductDetail';
import type { PdpEffectiveProductVm } from '../viewModels/pdp.vm';
import { normalizeNumericField } from '@/features/pdp/utils/normalizeNumericField';

function getImageUrlFromMediaItems(mediaItems: Record<string, unknown>): string {
  const carouselSeq = (mediaItems as { carouselSeq?: unknown }).carouselSeq;
  if (!Array.isArray(carouselSeq)) return '';
  for (const item of carouselSeq) {
    if (!item || typeof item !== 'object') continue;
    const typed = item as { type?: string; urlMap?: Record<string, string> };
    if (typed.type !== 'image' || !typed.urlMap) continue;
    return typed.urlMap['1024'] ?? typed.urlMap['828'] ?? typed.urlMap['70'] ?? '';
  }
  return '';
}

export function getFirstMediaImageUrl(mediaItems: Record<string, unknown>): string {
  const carouselSeq = (mediaItems as { carouselSeq?: unknown }).carouselSeq;
  if (!Array.isArray(carouselSeq) || carouselSeq.length === 0) return '';
  const first = carouselSeq[0] as { urlMap?: Record<string, string> } | undefined;
  return first?.urlMap?.['70'] ?? '';
}

/**
 * Builds the standard product attribute map used across PDP WebEngage events.
 * Mirrors getProductAttributes() from the old app's eventLogger.js.
 */
export function buildPdpProductAttributes(
  product: ProductDetail,
  vm: PdpEffectiveProductVm,
  selectedSizeDisplay?: string,
): Record<string, unknown> {
  const imageUrl = getImageUrlFromMediaItems(product.mediaItems);

  const stonesArr: string[] = [];
  if (vm.diamondStoneType) stonesArr.push('Diamond');
  const solitaireCarat = vm.solitaireCarat ?? product.solitaireCarat;
  if (solitaireCarat) stonesArr.push('Solitaire');
  if (product.gemstones) stonesArr.push(product.gemstones);
  const stonesName = stonesArr.join(',');

  const attrs: Record<string, unknown> = {
    'Product Id': String(product.designId),
    'Customization Id': String(vm.activeCustomizationId),
    'Product Name': product.designName ?? '',
    'Category Name': product.category ?? '',
    'MRP': vm.price ?? 0,
    'Selling Price': vm.discountedPrice ?? 0,
    'Discount': vm.discountValue ?? 0,
    'Offer Name': vm.discountName ?? '',
    'Collection Name': product.collectionName ?? '',
    'Color': vm.metal ?? '',
    'Image': imageUrl,
    'Metal Type': vm.metalType ?? '',
    'Metal Purity': vm.goldPurity ?? '',
    'Metal Weight': vm.metalWeight ?? '',
    'Product Weight': vm.productWeight ?? '',
    'Currency': 'INR',
    'Error Flag': !selectedSizeDisplay,
    'Size': selectedSizeDisplay ?? '',
  };

  if (product.productHeight != null) {
    const height = normalizeNumericField(product.productHeight);
    if (height != null) attrs['Height'] = height;
  }
  if (product.productWidth != null) {
    const width = normalizeNumericField(product.productWidth);
    if (width != null) attrs['Width'] = width;
  }
  if (product.productLength != null) {
    const length = normalizeNumericField(product.productLength);
    if (length != null) attrs['Length'] = length;
  }
  if (product.diamondCarat != null) attrs['Diamond Weight'] = product.diamondCarat;
  if (vm.diamondStoneType) attrs['Diamond Color'] = vm.diamondStoneType;
  if (product.noOfDiamonds != null) attrs['No. of Diamonds'] = product.noOfDiamonds;
  if (stonesName) attrs['Stones'] = stonesName;
  if (product.gemstonesTypeAndCount) attrs['No. of other stones'] = product.gemstonesTypeAndCount;
  if (product.solitaireClarity) attrs['Solitaire Clarity'] = product.solitaireClarity;
  if (product.solitaireColor) attrs['Solitaire Color'] = product.solitaireColor;
  if (solitaireCarat) attrs['Solitaire Weight'] = solitaireCarat;
  if (product.solitaireTotalNoOfStones != null) attrs['No. of Solitaire'] = product.solitaireTotalNoOfStones;

  return attrs;
}
